/**
 * Authoring spec — the on-disk / API protocol shape the Studio reads and writes.
 *
 * This is the SINGLE source of truth for the wire contract. It mirrors
 * `tricorder-protos/proto/tricorder/protocol/v1/protocol.proto` rendered as **camelCase
 * protojson with empty fields omitted** — byte-compatible with the protocol
 * specs stored in the protocols.spec JSONB column and served by the protocol
 * read/write API.
 *
 * The Go loader (`tricorder-go/protocol/loader.go`) runs a STRICT
 * unknown-field check (it rejects any field except a top-level `state` block),
 * so the serializer must emit exactly these keys and nothing else. Editor-only
 * data (canvas positions) lives outside the spec entirely — see ydoc.ts.
 */

// ── Enum string unions (proto enum names, on the wire verbatim) ──────────────

export const NODE_KINDS = [
  'NODE_KIND_STEP',
  'NODE_KIND_DECISION',
  'NODE_KIND_OBSERVATION',
  'NODE_KIND_ACTION',
  'NODE_KIND_TERMINAL',
  // Complex-model kinds (additive). They advance through the graph via guarded
  // edges exactly like STEP/DECISION; the kind is a visual/semantic classifier
  // so the Studio can author + render non-linear structure. The execution
  // behaviour they imply lives in their outgoing edge guards (see spec helpers
  // timeGuard / stateGuard below and the proto NodeKind comment).
  'NODE_KIND_FORK',
  'NODE_KIND_JOIN',
  'NODE_KIND_LOOP',
  'NODE_KIND_TIMER',
  'NODE_KIND_EVENT',
  'NODE_KIND_COUNTER',
] as const
export type NodeKind = (typeof NODE_KINDS)[number]

export const ACTION_KINDS = [
  'ACTION_KIND_CONFIRM',
  'ACTION_KIND_CHOICE',
  'ACTION_KIND_OBSERVATION_INPUT',
] as const
export type ActionKind = (typeof ACTION_KINDS)[number]

export const TONES = [
  'TONE_UNSPECIFIED',
  'TONE_OK',
  'TONE_WARN',
  'TONE_BAD',
  'TONE_MUTED',
] as const
export type Tone = (typeof TONES)[number]

export const OBSERVATION_VALUE_TYPES = [
  'OBSERVATION_VALUE_TYPE_STRING',
  'OBSERVATION_VALUE_TYPE_INTEGER',
  'OBSERVATION_VALUE_TYPE_FLOAT',
  'OBSERVATION_VALUE_TYPE_BOOLEAN',
] as const
export type ObservationValueType = (typeof OBSERVATION_VALUE_TYPES)[number]

// ── Spec shape ───────────────────────────────────────────────────────────────

/**
 * A code in some taxonomy — `{system, code, display}`, mirroring proto Coding.
 * The taxonomy is open by decision (adr/0007): SNOMED CT, LOINC, ICD-10, CHOP
 * and a tenant's own scheme (`urn:tricorder:act`) are all expressible, and there
 * is no allowlist anywhere in the platform. `system` and `code` must both be
 * non-empty — a coding with either blank names nothing and the Go loader
 * rejects it.
 */
export interface SpecCoding {
  system: string
  code: string
  display?: string
}

export interface SpecAction {
  id: string
  label: string
  kind: ActionKind
  /** OBSERVATION_INPUT only. */
  valueType?: ObservationValueType
  payloadKey?: string
  unit?: string
  placeholder?: string
  /**
   * Optional coding of the QUANTITY this input captures (proto
   * Action.item_coding) — the pad set, the ampoule, the item counted. Carried
   * onto the effort entry as `activity.quantities[]` (adr/0008), which is how a
   * count reaches billing at all: a coded act says defibrillation happened and
   * says nothing about the three pad sets it took.
   */
  itemCoding?: SpecCoding
}

export interface SpecNodeContent {
  title: string
  verdict?: string
  tone?: Tone
  body?: string[]
  /** Lookup detail under the body (protocolpb.NodeContent.reference). */
  reference?: string
  actions?: SpecAction[]
}

export interface SpecNode {
  id: string
  kind: NodeKind
  /** BCP-47 locale tag → content. */
  locales: Record<string, SpecNodeContent>
  /** Optional id of the Phase this node belongs to (references SpecPhase.id).
   *  Empty / undefined = unphased — the runner falls back to a topology band. */
  phaseId?: string
  /** Optional id of the Actor (swimlane) this node is assigned to (references
   *  SpecActor.id). Empty / undefined = unassigned (no lane). Presentation-only. */
  actorId?: string
  /**
   * The author's declaration that this node represents a discrete clinical act
   * performed on the patient, as against a decision point, a navigation step or
   * a teaching step (proto Node.performed_act). It is the CONTROL flag: it, and
   * not the presence of a coding, is what makes advancing this node write an
   * effort entry (adr/0007). A coding describes; a flag controls.
   */
  performedAct?: boolean
  /** What the step DOES — the procedure or intervention (proto Node.act_codings). */
  actCodings?: SpecCoding[]
  /**
   * The patient CONDITION the step addresses or establishes — the diagnosis,
   * the finding, the rhythm (proto Node.condition_codings). Its own slot rather
   * than a qualifier on the act, because it is meaningful on a node that
   * performs nothing, and because a code system alone cannot tell a procedure
   * from a finding.
   */
  conditionCodings?: SpecCoding[]
  /**
   * Marks a step as clinically essential (proto Node.essential) — read by the
   * run summary to distinguish the steps that had to happen from the rest.
   */
  essential?: boolean
  /**
   * The training component this node REFERENCES, as "<component_id>/<version>"
   * or bare "<component_id>" (is_current at run time) — proto Node.component_ref.
   * The curriculum profile's linchpin; absent everywhere else.
   */
  componentRef?: string
  /** The goals this step serves (proto Node.goal_ids, references SpecGoal.id). */
  goalIds?: string[]
  /**
   * The documents and passages this step comes from (proto Node.documentation,
   * FHIR PlanDefinition.action.documentation). A step modelled from a passage
   * carries it as RELATED_ARTIFACT_TYPE_DERIVED_FROM with its version and anchor.
   */
  documentation?: SpecRelatedArtifact[]
}

export interface SpecEdge {
  fromNodeId: string
  toNodeId: string
  /** CEL guard over {input, context}; empty = always-true (default edge). */
  when?: string
}

/**
 * SpecPhase is an ordered, named clinical band of the algorithm (e.g.
 * "CPR & Rhythmus"). Optional and purely additive — a protocol with no phases
 * renders with topology-derived bands. Order = array order in ProtocolSpec.phases.
 * Mirrors the proto Phase message (id + localized labels map).
 */
export interface SpecPhase {
  id: string
  /** BCP-47 locale tag → display label (localized, like SpecNode content). */
  labels: Record<string, string>
}

/**
 * SpecActor is a named participant a node is assigned to — a swimlane (e.g.
 * "Nurse", "Team leader", "Patient", "System"). Optional and purely additive,
 * mirroring SpecPhase: a protocol with no actors renders without lanes, and a
 * node with no actorId is simply unassigned. Order = array order in
 * ProtocolSpec.actors. Presentation-only — the runtime ignores actors.
 */
export interface SpecActor {
  id: string
  /** BCP-47 locale tag → display label (localized, like SpecPhase labels). */
  labels: Record<string, string>
  /** Optional reference to an org role (the /roles catalogue, CUJ-84). When set,
   *  this lane IS an organisational "form role" the author picked from the org
   *  environment, not a free-text participant (e.g. "Patient", "System") which
   *  carries no roleId. The id is the live reference; labels carry the display
   *  name. Mirrors the proto Actor.role_id. */
  roleId?: string
}

/**
 * SpecLocationRef binds the workflow to an org-hierarchy location (a unit from
 * the /locations catalogue, CUJ-84) — declaring WHERE the protocol is meant to
 * run. `id` is the live reference; `name` and `kind` are a snapshot taken at
 * authoring time so the lane still renders a meaningful label if the catalogue
 * is unavailable. Presentation/governance only — the runtime ignores it.
 * Mirrors the proto LocationRef.
 */
export interface SpecLocationRef {
  id: string
  name: string
  kind: string
}

export interface SpecSource {
  citation?: string
  url?: string
  reviewedAt?: string
  reviewer?: string
  /** The knowledge document the version was modelled from: its primary source
   *  (proto Source.source_document_id). Absent, never empty. */
  sourceDocumentId?: string
}

/** google.type.Decimal's string form, as tricorder-protocol's loader checks it. */
export const DECIMAL_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/

/**
 * How a recorded value compares with a goal's target value (proto
 * TargetComparator, FHIR Quantity.comparator). An exact target names none;
 * TARGET_COMPARATOR_EQUAL is deprecated and refused by the loader, so it is
 * not offered.
 */
export const TARGET_COMPARATORS = [
  'TARGET_COMPARATOR_GREATER_OR_EQUAL',
  'TARGET_COMPARATOR_GREATER_THAN',
  'TARGET_COMPARATOR_LESS_OR_EQUAL',
  'TARGET_COMPARATOR_LESS_THAN',
] as const
export type TargetComparator = (typeof TARGET_COMPARATORS)[number] | 'TARGET_COMPARATOR_EQUAL'

/** A target range, both bounds inclusive decimals, either open (proto TargetRange, FHIR Range). */
export interface SpecTargetRange {
  low?: string
  high?: string
}

/**
 * A goal held to a value an observation input records (proto
 * RecordedValueMeasure). Durations are protojson strings in seconds ("1800s").
 * Every field is optional: an unset reference is a step not yet modelled, an
 * unset comparator or value is a placeholder target.
 */
export interface SpecRecordedValueMeasure {
  nodeId?: string
  actionId?: string
  /** Absent with a value: the value is met exactly (FHIR Quantity). */
  comparator?: TargetComparator
  /** A decimal string, e.g. "4.0" (FHIR detailQuantity). */
  value?: string
  /** The target as a range instead of a value (FHIR detailRange); never with value or comparator. */
  range?: SpecTargetRange
  unit?: string
  /** How long after the run starts; absent: at any point in the run. */
  within?: string
}

/** A goal held to the time from one step to another (proto StepIntervalMeasure). */
export interface SpecStepIntervalMeasure {
  fromNodeId?: string
  toNodeId?: string
  /** The longest the interval may take; absent while it is a placeholder. */
  max?: string
}

/** A goal held to a step being reached (proto StepReachedMeasure). */
export interface SpecStepReachedMeasure {
  nodeId?: string
  /** How long after the run starts; absent: at any point in the run. */
  within?: string
}

/** A goal held to a criterion the author states in their own words (proto StatedMeasure). */
export interface SpecStatedMeasure {
  /** The criterion as the author states it; absent while it is a placeholder. */
  text?: string
}

/** How a run shows a goal was reached: exactly one kind (proto GoalMeasure oneof). */
export type SpecGoalMeasure =
  | { recordedValue: SpecRecordedValueMeasure }
  | { stepInterval: SpecStepIntervalMeasure }
  | { stepReached: SpecStepReachedMeasure }
  | { stated: SpecStatedMeasure }

/** Where a knowledge document states a goal; page and line count from 1 (proto SourceLine). */
export interface SpecSourceLine {
  documentId: string
  page: number
  line: number
}

/**
 * One outcome the process exists to reach (proto Goal): a statement, how a run
 * shows it was reached with its target, and the source line it came from.
 */
export interface SpecGoal {
  id: string
  text: string
  measure?: SpecGoalMeasure
  sourceLine?: SpecSourceLine
}

/** Why a version points at a document (proto RelatedArtifactType). */
export const RELATED_ARTIFACT_TYPES = [
  'RELATED_ARTIFACT_TYPE_CITATION',
  'RELATED_ARTIFACT_TYPE_DOCUMENTATION',
  'RELATED_ARTIFACT_TYPE_JUSTIFICATION',
  'RELATED_ARTIFACT_TYPE_DERIVED_FROM',
] as const
export type RelatedArtifactType = (typeof RELATED_ARTIFACT_TYPES)[number]

/**
 * A knowledge document the version cites, documents or justifies itself with
 * (proto RelatedArtifact, FHIR PlanDefinition.relatedArtifact). The version
 * carries them: a new version copies them with the spec, and a deleted
 * version takes them with it.
 */
export interface SpecRelatedArtifact {
  type: RelatedArtifactType
  documentId: string
  display: string
  /** The cited version of the document, from 1; absent cites it as it stands. */
  versionNo?: number
  /** The passage of that version (proto TextAnchor, W3C selectors over its Markdown). */
  anchor?: SpecTextAnchor
}

/** A passage, located as the W3C Web Annotation model locates text (proto TextAnchor). */
export interface SpecTextAnchor {
  quote: { exact: string; prefix?: string; suffix?: string }
  position?: { start: number; end: number }
}

export interface ProtocolSpec {
  id: string
  version: string
  source?: SpecSource
  defaultLocale: string
  locales: string[]
  entryNodeId: string
  nodes: SpecNode[]
  edges: SpecEdge[]
  /** Optional, ordered clinical phases — named bands the runner shows for
   *  "where am I". See SpecPhase. */
  phases?: SpecPhase[]
  /** Optional, ordered actors (swimlanes) — the participants nodes are assigned
   *  to. See SpecActor. */
  actors?: SpecActor[]
  /** Optional org-environment bindings — the organisational units (wards, ICUs,
   *  …) this workflow connects to. See SpecLocationRef. */
  locations?: SpecLocationRef[]
  /** Optional goals the steps cite by SpecNode.goalIds (proto Protocol.goals). */
  goals?: SpecGoal[]
  /** The documents the version cites besides its primary source (proto
   *  Protocol.related_artifacts). */
  relatedArtifacts?: SpecRelatedArtifact[]
  constants?: Record<string, string>
  /**
   * Opaque state-rules block (state_loader.go extension). Not modelled by the
   * proto; the Studio never edits it but round-trips it verbatim so editing a
   * draft that carries one never destroys it.
   */
  state?: unknown
}

// ── Defaults / helpers ───────────────────────────────────────────────────────

/** Default action kind for a node kind — the editor's node→action grammar. */
export function defaultActionKind(node: NodeKind): ActionKind {
  if (node === 'NODE_KIND_DECISION') return 'ACTION_KIND_CHOICE'
  if (node === 'NODE_KIND_OBSERVATION') return 'ACTION_KIND_OBSERVATION_INPUT'
  return 'ACTION_KIND_CONFIRM'
}

/**
 * The CEL guard the runtime expects for an edge taken when the user picks
 * `actionId`. Authors never type this — the Studio derives it from the action
 * the edge springs from. Matches the shipped protocols exactly
 * (`input.action_id == 'continue'`).
 */
export function actionGuard(actionId: string): string {
  return `input.action_id == '${actionId}'`
}

/** Extract the action id a guard tests, if it is the canonical action guard. */
export function guardActionId(when: string | undefined): string | null {
  if (!when) return null
  const m = when.match(/^input(?:\.action_id|\['action_id'\]) == '([^']+)'$/)
  return m ? m[1] : null
}

/**
 * The author has modelled something: more than the one step a new draft is
 * seeded with (initEmptyDoc), a titled step, or an edge. The untouched seed is
 * not content, and nothing — checks, coverage — nags about it.
 */
export function hasContent(spec: ProtocolSpec): boolean {
  return (
    spec.nodes.length !== 1 ||
    spec.edges.length > 0 ||
    Object.values(spec.nodes[0].locales).some((c) => Boolean(c.title?.trim()))
  )
}

// ── Edge triggers — the authoring grammar over CEL edge guards ────────────────
//
// The runtime advances on the FIRST matching edge whose CEL `when` is true. The
// Studio never makes the author type CEL: instead it offers typed *triggers* and
// derives the guard. Each trigger maps to a CEL expression the runtime already
// understands (no runtime change) — see the proto NodeKind comment:
//   • action → `input.action_id == 'x'`  (a choice, or an EVENT that arrived)
//   • time   → `(now - last_advance_at) >= duration('Ns')`  (a TIMER elapsed)
//   • state  → `state.<key> <op> <value>`  (a folded COUNTER / accumulated value)
//   • always → empty guard (default edge)
//   • custom → a raw CEL expression the builder doesn't recognise (round-tripped)

export const STATE_OPS = ['>=', '>', '==', '!=', '<=', '<'] as const
export type StateOp = (typeof STATE_OPS)[number]

export type EdgeTrigger =
  | { type: 'always' }
  | { type: 'action'; actionId: string }
  | { type: 'time'; seconds: number }
  | { type: 'state'; key: string; op: StateOp; value: string }
  | { type: 'custom'; when: string }

/** CEL guard for "N seconds have elapsed since the last advance" (TIMER). */
export function timeGuard(seconds: number): string {
  return `(now - last_advance_at) >= duration('${Math.max(0, Math.round(seconds))}s')`
}

/** Parse the elapsed-seconds a time guard tests, if it is the canonical form. */
export function guardElapsedSeconds(when: string | undefined): number | null {
  if (!when) return null
  const m = when.match(/^\(\s*now\s*-\s*last_advance_at\s*\)\s*>=\s*duration\('(\d+)s'\)$/)
  return m ? Number(m[1]) : null
}

/** A CEL identifier — keys matching this are safe to address as `state.<key>`. */
const STATE_IDENT_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/

/**
 * CEL guard for a folded-state / context threshold (COUNTER, accumulated value).
 * Keys that are valid CEL identifiers use dot access (`state.cycles`); any other
 * key (e.g. one with a hyphen) MUST use bracket access (`state['rhythm-check']`)
 * — `state.rhythm-check` would parse as the subtraction `state.rhythm - check`
 * and the Go loader would reject it at compile time.
 */
export function stateGuard(key: string, op: StateOp, value: string): string {
  // Bracket-quote any non-identifier key, escaping single quotes so a key like
  // `it's` (possible in a hand-authored state block) yields valid CEL rather than
  // a string the loader rejects.
  const ref = STATE_IDENT_RE.test(key) ? `state.${key}` : `state['${key.replace(/'/g, "\\'")}']`
  return `${ref} ${op} ${value}`
}

/** Parse a `state.<key> <op> <value>` (or `state['<key>'] …`) guard. */
export function guardState(
  when: string | undefined,
): { key: string; op: StateOp; value: string } | null {
  if (!when) return null
  const m = when.match(
    /^state(?:\.([a-zA-Z_][a-zA-Z0-9_]*)|\['([^']+)'\])\s*(>=|>|==|!=|<=|<)\s*(.+)$/,
  )
  if (!m) return null
  const key = m[1] ?? m[2]
  return { key, op: m[3] as StateOp, value: m[4].trim() }
}

/**
 * whenToTrigger classifies an edge's CEL guard into a typed trigger for the
 * builder. Order matters: action and time/state are mutually exclusive canonical
 * shapes; anything else round-trips verbatim as `custom` so hand-written CEL is
 * never silently dropped.
 */
export function whenToTrigger(when: string | undefined): EdgeTrigger {
  const w = when?.trim()
  if (!w) return { type: 'always' }
  const actionId = guardActionId(w)
  if (actionId !== null) return { type: 'action', actionId }
  const seconds = guardElapsedSeconds(w)
  if (seconds !== null) return { type: 'time', seconds }
  const st = guardState(w)
  if (st) return { type: 'state', ...st }
  return { type: 'custom', when: w }
}

/** triggerToWhen derives the CEL guard for a typed trigger (inverse of whenToTrigger). */
export function triggerToWhen(trigger: EdgeTrigger): string {
  switch (trigger.type) {
    case 'always':
      return ''
    case 'action':
      return actionGuard(trigger.actionId)
    case 'time':
      return timeGuard(trigger.seconds)
    case 'state':
      return stateGuard(trigger.key, trigger.op, trigger.value)
    case 'custom':
      return trigger.when
  }
}

/**
 * phaseLabel resolves a phase's display label for `locale`, falling back to the
 * first non-empty label, then the id. Keeps every phase-rendering surface (the
 * editor, the node picker, the canvas chip) consistent.
 */
export function phaseLabel(p: SpecPhase, locale: string): string {
  const exact = p.labels?.[locale]
  if (exact && exact.trim()) return exact
  for (const v of Object.values(p.labels ?? {})) {
    if (v && v.trim()) return v
  }
  return p.id
}

/**
 * actorLabel resolves an actor's display label for `locale`, falling back to the
 * first non-empty label, then the id. Mirrors phaseLabel so every actor-rendering
 * surface (canvas lane header, node chip, picker) stays consistent.
 */
export function actorLabel(a: SpecActor, locale: string): string {
  const exact = a.labels?.[locale]
  if (exact && exact.trim()) return exact
  for (const v of Object.values(a.labels ?? {})) {
    if (v && v.trim()) return v
  }
  return a.id
}

// ── Serialization (editor model → spec) ──────────────────────────────────────

function nonEmpty(s: string | undefined | null): s is string {
  return typeof s === 'string' && s.trim().length > 0
}

/**
 * A coding names something only when it carries BOTH a system and a code, so a
 * row missing either is dropped here rather than emitted.
 *
 * Dropping is the safe half of a real trade-off. Saving a draft runs the same
 * `tricorder-go/protocol/loader.go` validate() that publishing does, and its
 * `validateCodings` rejects the WHOLE spec over one blank field — so emitting a
 * half-typed row would make the author unable to save anything at all. Nothing
 * is dropped silently: `validateSpec` reports the same row as an error, in the
 * editor, before the save.
 *
 * `system` is never inspected beyond being non-empty. There is no allowlist of
 * code systems, here or anywhere else in the platform (adr/0007).
 */
function serializeCoding(c: SpecCoding | undefined): SpecCoding | null {
  if (!c || !nonEmpty(c.system) || !nonEmpty(c.code)) return null
  const out: SpecCoding = { system: c.system, code: c.code }
  if (nonEmpty(c.display)) out.display = c.display
  return out
}

function serializeCodings(list: SpecCoding[] | undefined): SpecCoding[] {
  const out: SpecCoding[] = []
  for (const c of list ?? []) {
    const coding = serializeCoding(c)
    if (coding) out.push(coding)
  }
  return out
}

/** A related artifact as protojson writes it: version and anchor only when set. */
function serializeArtifact(r: SpecRelatedArtifact): SpecRelatedArtifact {
  const out: SpecRelatedArtifact = { type: r.type, documentId: r.documentId, display: r.display }
  if (r.versionNo) out.versionNo = r.versionNo
  if (r.anchor) {
    const quote: SpecTextAnchor['quote'] = { exact: r.anchor.quote.exact }
    if (nonEmpty(r.anchor.quote.prefix)) quote.prefix = r.anchor.quote.prefix
    if (nonEmpty(r.anchor.quote.suffix)) quote.suffix = r.anchor.quote.suffix
    out.anchor = { quote }
    if (r.anchor.position) out.anchor.position = { start: r.anchor.position.start, end: r.anchor.position.end }
  }
  return out
}

function serializeAction(a: SpecAction): SpecAction {
  const out: SpecAction = { id: a.id, label: a.label } as SpecAction
  // An action with no kind is read back without the field (proto3 omits the
  // zero enum), and actionFromWire spells that "". Sent back as "", the
  // service refuses the whole spec ("invalid value for enum field kind"), so
  // an unset kind stays unset on the wire.
  if (a.kind) out.kind = a.kind
  if (a.kind === 'ACTION_KIND_OBSERVATION_INPUT') {
    if (a.valueType) out.valueType = a.valueType
    if (nonEmpty(a.payloadKey)) out.payloadKey = a.payloadKey
    if (nonEmpty(a.unit)) out.unit = a.unit
    if (nonEmpty(a.placeholder)) out.placeholder = a.placeholder
    // The coding of the quantity this input captures (adr/0008) — how a count
    // of pad sets or ampoules reaches the effort entry. Emitted only for
    // OBSERVATION_INPUT, matching the proto's "ignored for non-INPUT kinds".
    const itemCoding = serializeCoding(a.itemCoding)
    if (itemCoding) out.itemCoding = itemCoding
  }
  return out
}

function serializeContent(c: SpecNodeContent): SpecNodeContent {
  const out: SpecNodeContent = { title: c.title ?? '' }
  if (nonEmpty(c.verdict)) out.verdict = c.verdict
  if (c.tone && c.tone !== 'TONE_UNSPECIFIED') out.tone = c.tone
  // Body lines are content — a "" is a deliberate spacer (see
  // cardiac-arrest-pregnancy). Preserve them verbatim; only drop the field
  // when there are no lines at all.
  const body = c.body ?? []
  if (body.length) out.body = body
  if (nonEmpty(c.reference)) out.reference = c.reference
  const actions = (c.actions ?? []).map(serializeAction)
  if (actions.length) out.actions = actions
  return out
}

/**
 * serializeSpec produces the canonical authoring JSON: camelCase keys, empty
 * fields omitted, byte-compatible with the shipped on-disk protocols. This is
 * the ONLY function that converts the editor model to the wire shape.
 */
export function serializeSpec(spec: ProtocolSpec): ProtocolSpec {
  const out: ProtocolSpec = {
    id: spec.id,
    version: spec.version,
    defaultLocale: spec.defaultLocale,
    locales: spec.locales,
    entryNodeId: spec.entryNodeId,
    nodes: spec.nodes.map((n) => {
      const node: SpecNode = {
        id: n.id,
        kind: n.kind,
        locales: Object.fromEntries(
          Object.entries(n.locales).map(([loc, c]) => [loc, serializeContent(c)]),
        ),
      }
      // phaseId is optional + omitted when empty (a node belongs to at most one
      // phase; unphased nodes carry no key).
      if (nonEmpty(n.phaseId)) node.phaseId = n.phaseId
      // actorId is optional + omitted when empty (a node belongs to at most one
      // actor/lane; unassigned nodes carry no key).
      if (nonEmpty(n.actorId)) node.actorId = n.actorId
      // The clinical declarations (adr/0007) and the two additive fields that
      // preceded them. Every one of these is authored elsewhere too — by the
      // assistant, by an imported spec, by hand — and this function is the only
      // path from editor to wire, so a field missing here is deleted from the
      // protocol the first time somebody opens it in Studio and saves. That is
      // how they were lost: `performedAct` is what makes advancing a step write
      // an effort entry at all, so the protocol kept rendering and simply
      // stopped producing billing evidence.
      //
      // `performedAct` is the CONTROL and the codings only DESCRIBE — nothing
      // here may derive one from the other (a coding added for search would
      // start asserting that a procedure was performed on a patient).
      if (n.performedAct) node.performedAct = true
      const actCodings = serializeCodings(n.actCodings)
      if (actCodings.length) node.actCodings = actCodings
      const conditionCodings = serializeCodings(n.conditionCodings)
      if (conditionCodings.length) node.conditionCodings = conditionCodings
      if (n.essential) node.essential = true
      if (nonEmpty(n.componentRef)) node.componentRef = n.componentRef
      if (n.goalIds && n.goalIds.length) node.goalIds = n.goalIds
      if (n.documentation && n.documentation.length) node.documentation = n.documentation.map(serializeArtifact)
      return node
    }),
    edges: spec.edges.map((e) => {
      const edge: SpecEdge = { fromNodeId: e.fromNodeId, toNodeId: e.toNodeId }
      if (nonEmpty(e.when)) edge.when = e.when
      return edge
    }),
  }

  const source = spec.source
  if (
    source &&
    (nonEmpty(source.citation) ||
      nonEmpty(source.url) ||
      nonEmpty(source.reviewer) ||
      nonEmpty(source.reviewedAt) ||
      nonEmpty(source.sourceDocumentId))
  ) {
    out.source = {}
    if (nonEmpty(source.citation)) out.source.citation = source.citation
    if (nonEmpty(source.url)) out.source.url = source.url
    if (nonEmpty(source.reviewedAt)) out.source.reviewedAt = source.reviewedAt
    if (nonEmpty(source.reviewer)) out.source.reviewer = source.reviewer
    if (nonEmpty(source.sourceDocumentId)) out.source.sourceDocumentId = source.sourceDocumentId
  }

  const constants = spec.constants ?? {}
  if (Object.keys(constants).length) out.constants = constants

  // Phases: ordered bands, empty labels dropped. Omitted entirely when none —
  // the protocol then renders with topology-derived bands.
  const phases = spec.phases ?? []
  if (phases.length) {
    out.phases = phases.map((p) => {
      const labels: Record<string, string> = {}
      for (const [loc, v] of Object.entries(p.labels ?? {})) {
        if (nonEmpty(v)) labels[loc] = v
      }
      return { id: p.id, labels }
    })
  }

  // Actors: ordered swimlanes, empty labels dropped. Omitted entirely when none.
  // Mirrors the phases serialization exactly. roleId (the org-role reference) is
  // emitted only when set — a free-text lane carries no roleId.
  const actors = spec.actors ?? []
  if (actors.length) {
    out.actors = actors.map((a) => {
      const labels: Record<string, string> = {}
      for (const [loc, v] of Object.entries(a.labels ?? {})) {
        if (nonEmpty(v)) labels[loc] = v
      }
      const actor: SpecActor = { id: a.id, labels }
      if (nonEmpty(a.roleId)) actor.roleId = a.roleId
      return actor
    })
  }

  // Locations: ordered org-environment bindings, omitted entirely when none.
  // id is the live /locations reference; name/kind are display snapshots.
  const locations = spec.locations ?? []
  if (locations.length) {
    out.locations = locations.map((l) => ({ id: l.id, name: l.name, kind: l.kind }))
  }

  // Goals are authored in the Studio's Purpose step; related artifacts outside
  // it (the Manage drawer's linked documents) and carried verbatim. Both are
  // omitted when none.
  const goals = spec.goals ?? []
  if (goals.length) out.goals = goals.map(goalToWire)
  const related = spec.relatedArtifacts ?? []
  if (related.length) out.relatedArtifacts = related.map(serializeArtifact)

  if (spec.state !== undefined && spec.state !== null) out.state = spec.state

  return out
}

// ── Deserialization (wire → editor model) ────────────────────────────────────
//
// The inverse of serializeSpec. The server answers protojson, which omits every
// field holding its zero value: an untitled step arrives with no `title`, an
// unlabelled action with no `label`, a spec without phases with no `phases`.
// The proto zero value of a string is "" — that is what an omitted string
// field MEANS on the wire, not a value invented here — so this is the one
// place it is restored, and every reader past it can rely on the editor model's
// required fields being present.

type WireObject = Record<string, unknown>

function isObject(v: unknown): v is WireObject {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function wireString(o: WireObject, key: string): string {
  const v = o[key]
  return typeof v === 'string' ? v : ''
}

function wireList(o: WireObject, key: string): unknown[] {
  const v = o[key]
  return Array.isArray(v) ? v : []
}

function actionFromWire(raw: unknown): SpecAction {
  const a = isObject(raw) ? raw : {}
  return { ...(a as Partial<SpecAction>), id: wireString(a, 'id'), label: wireString(a, 'label'), kind: wireString(a, 'kind') as ActionKind }
}

function contentFromWire(raw: unknown): SpecNodeContent {
  const c = isObject(raw) ? raw : {}
  const out: SpecNodeContent = { ...(c as Partial<SpecNodeContent>), title: wireString(c, 'title') }
  if (Array.isArray(c.actions)) out.actions = c.actions.map(actionFromWire)
  return out
}

function nodeFromWire(raw: unknown): SpecNode {
  const n = isObject(raw) ? raw : {}
  const locales = isObject(n.locales) ? n.locales : {}
  return {
    ...(n as Partial<SpecNode>),
    id: wireString(n, 'id'),
    kind: wireString(n, 'kind') as NodeKind,
    locales: Object.fromEntries(Object.entries(locales).map(([loc, c]) => [loc, contentFromWire(c)])),
  }
}

/** Drops every empty string, as protojson omits a zero value. */
function compact<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== '')) as T
}

/**
 * A goal's measure and source line as the save sends them. As with a coding
 * (serializeCoding), a half-typed part is left out rather than emitted: the
 * loader refuses the WHOLE spec over one non-decimal value, a zero minute, a
 * range whose low is above its high, a stated measure that states nothing, or
 * a page without its line, so emitting
 * one would leave the author unable to save the draft at all. Nothing is left
 * out silently: validateSpec reports the same part as an error in the editor.
 * An unset part is a placeholder, which the loader accepts.
 */
const decimalOr = (v: string | undefined) => (v !== undefined && DECIMAL_RE.test(v) ? v : undefined)
const positiveOr = (d: string | undefined) => (d !== undefined && Number(d.replace(/s$/, '')) > 0 ? d : undefined)
const lineNumber = (n: number) => Number.isInteger(n) && n >= 1

function measureToWire(m: SpecGoalMeasure): SpecGoalMeasure | undefined {
  if ('recordedValue' in m) {
    const { range, value, comparator, within, ...rest } = m.recordedValue
    const out: SpecRecordedValueMeasure = { ...compact(rest), ...(positiveOr(within) ? { within } : {}) }
    if (range) {
      const low = decimalOr(range.low)
      const high = decimalOr(range.high)
      const ordered = low === undefined || high === undefined || Number(low) <= Number(high)
      out.range = ordered ? compact({ low, high }) : {}
    } else {
      if (decimalOr(value) !== undefined) out.value = value
      if (comparator && comparator !== 'TARGET_COMPARATOR_EQUAL') out.comparator = comparator
    }
    return { recordedValue: out }
  }
  if ('stepInterval' in m) {
    const { max, ...rest } = m.stepInterval
    const si = compact(rest)
    if (si.fromNodeId && si.fromNodeId === si.toNodeId) delete si.toNodeId
    return { stepInterval: { ...si, ...(positiveOr(max) ? { max } : {}) } }
  }
  if ('stepReached' in m) {
    const { within, ...rest } = m.stepReached
    return { stepReached: { ...compact(rest), ...(positiveOr(within) ? { within } : {}) } }
  }
  // A stated measure has no placeholder: the loader refuses a blank text, so
  // one with nothing stated is left out whole, as a half-typed source line is.
  const text = m.stated.text?.trim()
  return text ? { stated: { text } } : undefined
}

function goalToWire(g: SpecGoal): SpecGoal {
  const out: SpecGoal = { id: g.id, text: g.text }
  const measure = g.measure ? measureToWire(g.measure) : undefined
  if (measure) out.measure = measure
  const sl = g.sourceLine
  if (sl && sl.documentId.trim() && lineNumber(sl.page) && lineNumber(sl.line)) out.sourceLine = { ...sl }
  return out
}

function measureFromWire(raw: unknown): SpecGoalMeasure | undefined {
  if (!isObject(raw)) return undefined
  if (isObject(raw.recordedValue)) return { recordedValue: raw.recordedValue as SpecRecordedValueMeasure }
  if (isObject(raw.stepInterval)) return { stepInterval: raw.stepInterval as SpecStepIntervalMeasure }
  if (isObject(raw.stepReached)) return { stepReached: raw.stepReached as SpecStepReachedMeasure }
  if (isObject(raw.stated)) return { stated: raw.stated as SpecStatedMeasure }
  return undefined
}

function goalFromWire(raw: unknown): SpecGoal {
  const g = isObject(raw) ? raw : {}
  const out: SpecGoal = { id: wireString(g, 'id'), text: wireString(g, 'text') }
  const measure = measureFromWire(g.measure)
  if (measure) out.measure = measure
  if (isObject(g.sourceLine)) {
    const sl = g.sourceLine
    out.sourceLine = {
      documentId: wireString(sl, 'documentId'),
      page: typeof sl.page === 'number' ? sl.page : 0,
      line: typeof sl.line === 'number' ? sl.line : 0,
    }
  }
  return out
}

/**
 * specFromWire reads the authoring spec of a version read
 * (GET /protocols/{id}/versions/{v}, its `spec`) into the editor model. It is
 * the ONLY function that converts the wire shape to the editor model.
 */
export function specFromWire(raw: unknown, version: string): ProtocolSpec {
  const o = isObject(raw) ? raw : {}
  return {
    id: wireString(o, 'id'),
    version,
    defaultLocale: wireString(o, 'defaultLocale'),
    locales: wireList(o, 'locales').filter((x): x is string => typeof x === 'string'),
    entryNodeId: wireString(o, 'entryNodeId'),
    nodes: wireList(o, 'nodes').map(nodeFromWire),
    edges: wireList(o, 'edges') as SpecEdge[],
    phases: wireList(o, 'phases') as SpecPhase[],
    actors: wireList(o, 'actors') as SpecActor[],
    locations: wireList(o, 'locations') as SpecLocationRef[],
    source: isObject(o.source) ? (o.source as SpecSource) : undefined,
    goals: wireList(o, 'goals').map(goalFromWire),
    relatedArtifacts: wireList(o, 'relatedArtifacts') as SpecRelatedArtifact[],
    constants: isObject(o.constants) ? (o.constants as Record<string, string>) : undefined,
    state: o.state,
  }
}
